import {
  HOST_QUERY_KEY,
  isGov,
  metaApiInstance,
  QUERY_KEY,
  type ServiceAppState,
  TOKEN_SCOPE,
} from '@kc-console/context';
import { getConsoleBaseUrl, STATUS_CODE } from '@kc-console/core';

import { commonGuardErrorHandler } from '../../utils/commonGuardErrorHandler';
import { getRequestUrlData } from '../../utils/getRequestUrlData';
import { getServerTraceHttpTimingOptions } from '../../utils/serverTrace/store';

import type { Guard } from '../../types';

const getDefaultRegion = (accessibleRegions: string[]) => {
  return isGov() ? accessibleRegions[accessibleRegions.length - 1] : accessibleRegions[0];
};

/**
 * Region을 결정합니다.
 * @description
 * - project 스코프 서비스에 한하여 region을 확실하게 결정하기 위해서는 다음의 우선순위를 기반으로 리전을 결정합니다.
 * - url query params에 존재하는 region정보 (determineBaseInfoGuard에서 region state로 저장됨) = 1순위
 * - 최근에 방문한 리전 중 첫번째 = 2순위
 * - 접근 가능한 리전 중 첫번째 = 3순위
 * - 만약 이 모든 경우가 없다면 Internal Server Error로 처리
 */
export const determineRegionGuard: Guard<ServiceAppState> = async ({
  context,
  state,
  queryClient,
}) => {
  const { scope, token = '', projectId } = state;
  const { urlObj, region: queryRegion } = getRequestUrlData(context);

  const { fetchRecentRegion, fetchAccessibleRegion } = metaApiInstance({
    token,
    ...getServerTraceHttpTimingOptions(context.req, 'config'),
  });

  // project scope 서비스가 아니라면 필요없는 로직이므로 바로 가드 종료
  if (scope !== TOKEN_SCOPE.PROJECT) return { status: STATUS_CODE.OK };

  try {
    const recentRegions = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_RECENT_REGION],
      queryFn: fetchRecentRegion,
    });

    const accessibleRegions = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_ACCESSIBLE_REGION],
      queryFn: fetchAccessibleRegion,
    });

    const defaultRegion = getDefaultRegion(accessibleRegions);

    // accessibleRegions 이거 없거나 실패하는 케이스 무엇인가? -> 500 으로 보낸다.
    // -> 사용자가 접근 가능한 프로젝트는 있지만 accessibleRegions 가 없는 케이스는 iam 에서 프로젝트에 접근 가능리전 세팅 안한경우.
    if (accessibleRegions.length <= 0) {
      return { status: STATUS_CODE.INTERNAL_SERVER_ERROR };
    }

    const targetRegion = queryRegion || recentRegions[0] || defaultRegion;

    //유효하지 않은 Region을 입력하였을 시 accessibleRegion의 첫번째 리전으로 redirect합니다.
    if (!accessibleRegions.includes(targetRegion)) {
      urlObj.searchParams.set(QUERY_KEY.PROJECT_ID, projectId);
      urlObj.searchParams.set(QUERY_KEY.REGION, defaultRegion);

      return {
        redirect: {
          destination: `${getConsoleBaseUrl(urlObj.origin)}/dashboard/home${urlObj.search}`,
          permanent: false,
        },
      };
    }

    const isRegionForced = !queryRegion && !recentRegions[0];

    return { region: targetRegion, status: STATUS_CODE.OK, isRegionForced };
  } catch (error) {
    return commonGuardErrorHandler({
      context,
      state,
      error,
    });
  }
};

determineRegionGuard.displayName = 'determineRegionGuard';
